import React, { useState, useEffect } from 'react'
import '../styles/dashboard.css'
import Sidebar from '../components/Sidebar'
import Header from '../components/Header'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers, faUserGraduate, faFileAlt, faPassport,
  faList, faEdit, faTrash,
  faUserTie, faPhone, faIdCard, faArrowRight,
} from "@fortawesome/free-solid-svg-icons";

const API_URL = process.env.REACT_APP_API_URL || 'https://unilinkapi-production.up.railway.app';

const getStatusStyle = (status) => {
  const styles = {
    'In Progress': { background: '#ffedd5', color: '#9a3412' },
    'Pending':   { background: '#fef9c3', color: '#854d0e' },
    'Approved':  { background: '#dcfce7', color: '#166534' },
    'Accepted':  { background: '#dbeafe', color: '#1e40af' },
    'Rejected':  { background: '#fee2e2', color: '#991b1b' },
    'Withdrawn': { background: '#f3e8ff', color: '#6b21a8' },
    'Closed':    { background: '#f3f4f6', color: '#374151' },
  };
  return styles[status] || { background: '#f3f4f6', color: '#374151' };
};

function StudentDashboard({ user }) {
  const firstName = (user?.name || 'Student').trim().split(/\s+/)[0];

  return (
    <>
      <Sidebar />
      <div className="main-content" id="mainContent">
        <Header />
        <div className="dashboard-container">
          <div className="page-header">
            <div>
              <h1 className="page-title">Welcome, {firstName}</h1>
              <p className="page-subtitle">Manage your registration, applications, and visa from your student portal.</p>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-6 col-md-8">
              <Link
                to="/my-registration"
                style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
              >
                <div
                  style={{
                    background: 'linear-gradient(135deg, #fff7ed 0%, #ffffff 55%)',
                    border: '2px solid #fed7aa',
                    borderRadius: '16px',
                    padding: '28px 26px',
                    boxShadow: '0 8px 24px rgba(244,123,32,0.12)',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 12px 28px rgba(244,123,32,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(244,123,32,0.12)';
                  }}
                >
                  <div style={{
                    width: '52px', height: '52px', borderRadius: '14px',
                    background: 'linear-gradient(135deg, #F47B20, #e8660f)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', marginBottom: '16px',
                  }}>
                    <FontAwesomeIcon icon={faIdCard} style={{ fontSize: '22px' }} />
                  </div>
                  <h2 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: 700, color: '#111827' }}>
                    Student Registration Form
                  </h2>
                  <p style={{ margin: '0 0 18px', fontSize: '14px', color: '#6b7280', lineHeight: 1.5 }}>
                    Open and update your personal registration details, documents, and contact information.
                  </p>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    color: '#c2410c', fontWeight: 700, fontSize: '14px',
                  }}>
                    Open registration form <FontAwesomeIcon icon={faArrowRight} />
                  </span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalLeads: 0,
    newLeads: 0,
    assignedLeads: 0,
    totalStudents: 0,
    totalApplications: 0,
    pendingVisas: 0,
    approvedVisas: 0,
  });
  const [recentApplications, setRecentApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (user?.role === 'client') return;
    fetchDashboardData();
  }, [user?.role]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const [statsRes, appsRes] = await Promise.all([
        fetch(`${API_URL}/dashboard/stats`, { headers }),
        fetch(`${API_URL}/dashboard/recent-applications`, { headers })
      ]);
      const statsData = await statsRes.json();
      const appsData = await appsRes.json();
      if (statsData.success) setStats(statsData.stats);
      if (appsData.success) setRecentApplications(appsData.applications);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/applications/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setRecentApplications(prev => prev.filter(app => app.id !== id));
        setDeleteConfirm(null);
      }
    } catch (error) {
      console.error('Error deleting application:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (user?.role === 'client') {
    return <StudentDashboard user={user} />;
  }

  return (
    <>
      <Sidebar />
      <div className="main-content" id="mainContent">
        <Header />
        <div className="dashboard-container">
          <div className="page-header">
            <div>
              <h1 className="page-title">Dashboard Overview</h1>
              <p className="page-subtitle">Welcome back! Here's what's happening today.</p>
            </div>
          </div>

          <div className="row g-4 mb-4">
            <div className="col-lg-4 col-md-6">
              <Link to="/leads" className="stat-card-link" title="View all leads">
                <div className="stat-card stat-card-primary" style={{ cursor: 'pointer' }}>
                  <div className="stat-icon"><FontAwesomeIcon icon={faUsers} /></div>
                  <div className="stat-content">
                    <h3 className="stat-number">{loading ? '...' : (stats.totalLeads ?? 0).toLocaleString()}</h3>
                    <p className="stat-label">Total Leads</p>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-lg-4 col-md-6">
              <Link to="/leads?filter=new" className="stat-card-link" title="View new leads">
                <div className="stat-card stat-card-danger" style={{ cursor: 'pointer' }}>
                  <div className="stat-icon"><FontAwesomeIcon icon={faPhone} /></div>
                  <div className="stat-content">
                    <h3 className="stat-number">{loading ? '...' : (stats.newLeads ?? 0).toLocaleString()}</h3>
                    <p className="stat-label">New Leads</p>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-lg-4 col-md-6">
              <Link to="/leads?filter=assigned" className="stat-card-link" title="View assigned leads">
                <div className="stat-card stat-card-warning" style={{ '--card-color': '#F47B20', cursor: 'pointer' }}>
                  <div className="stat-icon"><FontAwesomeIcon icon={faUserTie} /></div>
                  <div className="stat-content">
                    <h3 className="stat-number">{loading ? '...' : (stats.assignedLeads ?? 0).toLocaleString()}</h3>
                    <p className="stat-label">Leads Assigned</p>
                  </div>
                </div>
              </Link>
            </div>
          </div>

          <div className="row g-4 mb-4">
            <div className="col-lg-4 col-md-6">
              <Link to="/students" className="stat-card-link" title="View registered students">
                <div className="stat-card stat-card-success" style={{ cursor: 'pointer' }}>
                  <div className="stat-icon"><FontAwesomeIcon icon={faUserGraduate} /></div>
                  <div className="stat-content">
                    <h3 className="stat-number">{loading ? '...' : (stats.totalStudents ?? 0).toLocaleString()}</h3>
                    <p className="stat-label">Total Students Registered</p>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-lg-4 col-md-6">
              <Link to="/applications" className="stat-card-link" title="View all applications">
                <div className="stat-card stat-card-info" style={{ cursor: 'pointer' }}>
                  <div className="stat-icon"><FontAwesomeIcon icon={faFileAlt} /></div>
                  <div className="stat-content">
                    <h3 className="stat-number">{loading ? '...' : (stats.totalApplications ?? 0).toLocaleString()}</h3>
                    <p className="stat-label">Total Applications</p>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-lg-4 col-md-6">
              <Link to="/visa?filter=approved" className="stat-card-link" title="View approved visas">
                <div className="stat-card stat-card-purple" style={{ cursor: 'pointer' }}>
                  <div className="stat-icon"><FontAwesomeIcon icon={faPassport} /></div>
                  <div className="stat-content">
                    <h3 className="stat-number">{loading ? '...' : (stats.approvedVisas ?? 0).toLocaleString()}</h3>
                    <p className="stat-label">Total Visa Approved</p>
                  </div>
                </div>
              </Link>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-12">
              <div className="chart-card">
                <div className="chart-header">
                  <h5 className="chart-title">
                    <FontAwesomeIcon icon={faList} className="me-2" />
                    Recent Applications
                  </h5>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Student Name</th>
                        <th>Register ID</th>
                        <th>Country</th>
                        <th>Program</th>
                        <th>Counselor Assigned</th>
                        <th>Status</th>
                        <th>Registration Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="8" className="text-center py-4">
                            <div className="spinner-border spinner-border-sm me-2" role="status" />
                            Loading...
                          </td>
                        </tr>
                      ) : recentApplications.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center py-4 text-muted">
                            No applications found
                          </td>
                        </tr>
                      ) : (
                        recentApplications.map(app => {
                          const statusStyle = getStatusStyle(app.application_status);
                          return (
                            <tr key={app.id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <img
                                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(app.student_name)}&background=random`}
                                    className="table-avatar me-2"
                                    alt={app.student_name}
                                  />
                                  <Link to={`/edit-application/${app.id}`} className="table-name-link">
                                    {app.student_name}
                                  </Link>
                                </div>
                              </td>
                              <td>
                                <span className="fw-semibold" style={{ color: '#F47B20' }}>
                                  {app.student_number || '-'}
                                </span>
                              </td>
                              <td>{app.country_name}</td>
                              <td>{app.program || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not set</span>}</td>
                              <td>
                                {app.counselor_names
                                  ? <span style={{ fontSize: '13px', fontWeight: '500' }}>{app.counselor_names}</span>
                                  : <span style={{ color: '#9ca3af', fontSize: '12px', fontStyle: 'italic' }}>Not Assigned</span>
                                }
                              </td>
                              <td>
                                <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', ...statusStyle }}>
                                  {app.application_status}
                                </span>
                              </td>
                              <td>{formatDate(app.created_at)}</td>
                              <td>
                                <div className="d-flex gap-1">
                                  <Link to={`/edit-application/${app.id}`} className="btn btn-sm btn-outline-primary" title="Edit">
                                    <FontAwesomeIcon icon={faEdit} />
                                  </Link>
                                  {deleteConfirm === app.id ? (
                                    <>
                                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(app.id)} title="Confirm Delete">✓</button>
                                      <button className="btn btn-sm btn-secondary" onClick={() => setDeleteConfirm(null)} title="Cancel">✕</button>
                                    </>
                                  ) : (
                                    <button className="btn btn-sm btn-outline-danger" onClick={() => setDeleteConfirm(app.id)} title="Delete">
                                      <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
